import i18n from "@/i18n";

/**
 * Localised money label for the few figures the widget formats itself (everything else arrives
 * server-rendered). Follows the backend's conventions so a client-formatted total sits next to a
 * server-formatted pot without a seam:
 *
 * - the bare narrow symbol, never a disambiguated one — "$1,110.50", not "US$1,110.50" (Intl
 *   prefixes the country outside the currency's home locale);
 * - a currency written as a code or an abbreviation gets a space before the amount
 *   ("SEK 1,110.50", "kr 1,110.50"), a symbol does not ("€500");
 * - plain spaces, not the no-break ones Intl emits;
 * - the WIDGET's language decides the locale, not the browser's, so the digits group and the
 *   decimal mark match the server-formatted pot beside it ("607,76" in French);
 * - the symbol always leads ("€607,76"), whatever the locale — one convention across the widget,
 *   the same one `currencySymbolFirst` applies to server-formatted strings.
 *
 * `minorUnits` keeps the cents on a whole figure ("€5.00") — for amounts that move in cents,
 * where "€235.9" beside "€264.10" reads as a mistake.
 */
export const formatMoney = (
  { value, currency }: { value: number; currency: string },
  { minorUnits = false }: { minorUnits?: boolean } = {}
): string => {
  const options: Intl.NumberFormatOptions = {
    style: "currency",
    currency,
    minimumFractionDigits: minorUnits ? 2 : 0,
    maximumFractionDigits: 2,
  };
  const locale = i18n.language || undefined;
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat(locale, {
      ...options,
      currencyDisplay: "narrowSymbol",
    }).format(value);
  } catch {
    try {
      // Engines without `narrowSymbol` throw a RangeError; the default display still beats
      // the raw fallback.
      formatted = new Intl.NumberFormat(locale, options).format(value);
    } catch {
      return `${value} ${currency}`;
    }
  }
  formatted = formatted.replace(/[\u00a0\u202f]/g, " ");
  // "SGD2.00" → "SGD 2.00", the backend's own rule for a leading bare code.
  const code = currency.toUpperCase();
  if (formatted.startsWith(code) && formatted.charAt(code.length) !== " ") {
    formatted = `${code} ${formatted.slice(code.length)}`;
  }
  return currencySymbolFirst(formatted);
};

/** One convention for every money figure the widget shows: the currency SYMBOL leads the amount
 *  with no space ("€2 500,00"), whatever side the locale puts it on — so a French pot, its
 *  ceiling badge and the home-card total all read the same way. Applied to server-formatted
 *  strings at the stream boundary and to the client's own `formatMoney`. Codes and words
 *  ("SEK 500", "100 kr") are not symbols and keep their place. */
export function currencySymbolFirst(label: string): string {
  const trailing = label.match(/^(.+?)\s?(\p{Sc})(\s.+)?$/u);
  if (!trailing) return label;
  return `${trailing[2]}${trailing[1]}${trailing[3] ?? ""}`;
}

/** The currency symbol of a money label, wherever the locale puts it, and the label without it:
 *  "€100 Cash" → { symbol: "€", rest: "100 Cash" }, "40,30 €" → { symbol: "€", rest: "40,30" },
 *  "40,30 € Cash" → { symbol: "€", rest: "40,30 Cash" }. `null` when there is no bare symbol
 *  (codes like "SEK 500" are not a glyph — a three-letter code is not a mark). Shared by the prize
 *  component and the badge, which both move the symbol into their mark slot. */
export function splitCurrencySymbol(
  label: string
): { symbol: string; rest: string } | null {
  const leading = label.match(/^(\p{Sc})\s?(.+)$/u);
  if (leading) return { symbol: leading[1], rest: leading[2] };
  // A trailing symbol ("40,30 €", "40,30 € Cash") is still a cash label; the mark leads
  // regardless — the widget's one convention (see `currencySymbolFirst`).
  const trailing = label.match(/^(.+?)\s?(\p{Sc})(\s.+)?$/u);
  if (trailing) {
    return { symbol: trailing[2], rest: trailing[1] + (trailing[3] ?? "") };
  }
  return null;
}

/** Applies `currencySymbolFirst` to every money object in a wire payload — anything shaped
 *  `{ value, currency, display }` (`MoneySchema`: pots, payouts, wins, ceilings, min. bets) —
 *  walking arrays and nested objects. Run once on each decoded message, so every screen reads the
 *  one convention without each component remembering to. Mutates and returns the payload. */
export function currencySymbolFirstDeep<T>(payload: T): T {
  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (node === null || typeof node !== "object") return;
    const obj = node as Record<string, unknown>;
    if (
      typeof obj.display === "string" &&
      "currency" in obj &&
      "value" in obj
    ) {
      obj.display = currencySymbolFirst(obj.display);
    }
    for (const value of Object.values(obj)) walk(value);
  };
  walk(payload);
  return payload;
}
